import { FunctionComponent, useEffect, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { faClock } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { TextArea } from "@/components/TextArea/TextArea";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import {
	COMPANY_ONLY_SCORE_NOTE,
	hasLeadScoringContent,
	LeadScoringPopover,
} from "@/components/LeadScoringPopover/LeadScoringPopover";

import { useUpdateOrganizationScoringMutation } from "@/pages/Private/redux/organization/organization.api";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { useAnchoredTooltip } from "@/utils/useAnchoredTooltip";

import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";

import { CompanyCategoryResults } from "../../Apollo/schema/get-people";

import { getTrackerBadgeScoringSource, isCompanyOnlyScore } from "./trackerBadgeScoring";

interface TrackerCompanyScoreEditorProps {
	organizationId: number;
	companyResult: CompanyCategoryResults | null | undefined;
}

const getScoreBadge = (companyResult: CompanyCategoryResults | null | undefined) => {
	if (!companyResult) {
		return null;
	}

	const { leadScore, result, companyCategory } = companyResult;

	if (leadScore === "1") {
		return "C1";
	}

	if (leadScore === "2") {
		return "C2";
	}

	if (leadScore === "3") {
		return "C3";
	}

	if (result === "Unsure") {
		return "?";
	}

	if (
		result === "Competitor" ||
		result === "Disqualified" ||
		companyCategory === "Disqualified" ||
		leadScore === "Disqualified"
	) {
		return "DQ";
	}

	return null;
};

const getBadgeClassName = (score: string | null) => {
	if (score === "C1") {
		return "cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-success border border-text-success font-bold bg-background-success rounded-full justify-center items-center";
	}

	if (score === "C2") {
		return "cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-warning border border-text-warning font-bold bg-background-warning rounded-full justify-center items-center";
	}

	if (score === "C3") {
		return "cursor-pointer w-[24px] h-[24px] border border-error text-error text-xxs font-bold bg-error-light rounded-full justify-center items-center flex";
	}

	if (score === "?") {
		return "cursor-pointer w-[24px] h-[24px] text-text-meta text-xs font-bold bg-border-light rounded-full justify-center items-center flex";
	}

	if (score === "DQ") {
		return "cursor-pointer w-[24px] h-[24px] text-white text-xxs font-bold bg-error rounded-full justify-center items-center flex";
	}

	return "";
};

export const TrackerCompanyScoreEditor: FunctionComponent<TrackerCompanyScoreEditorProps> = ({
	organizationId,
	companyResult,
}) => {
	const { data: score } = useGetScoreQuery();
	const [updateOrganizationScoring, { isLoading }] = useUpdateOrganizationScoringMutation();

	const [isVisible, setIsVisible] = useState(false);
	const [reason, setReason] = useState("");
	const [selectedCompanyCategory, setSelectedCompanyCategory] = useState<{
		id: number;
		title: string;
	} | null>(null);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (companyResult?.scoreCompanyCategory || companyResult?.companyCategory) {
			setSelectedCompanyCategory({
				id: companyResult?.scoreCompanyCategory?.id ?? 0,
				title:
					companyResult?.scoreCompanyCategory?.category || companyResult?.companyCategory || "",
			});
		}
	}, [companyResult]);

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useAnchoredTooltip(triggerRef, tooltipRef, isVisible);

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		await updateOrganizationScoring({
			organizationId,
			scoreCompanyCategoryId: selectedCompanyCategory?.id || 0,
			companyResult: selectedCompanyCategory?.title || "",
			reason,
		}).unwrap();

		setIsVisible(false);
		setReason("");
	};

	const scoreBadge = getScoreBadge(companyResult);
	const badgeClassName = getBadgeClassName(scoreBadge);

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[300px]"
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="flex flex-col gap-2">
					<div>
						<Dropdown
							data={[
								...(score?.prospectCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString() ?? ""})`,
								})) || []),
								...(score?.partnerCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString() ?? ""})`,
								})) || []),
								{
									id: 0,
									title: "Disqualified",
								},
								{
									id: 0,
									title: "Competitor",
								},
							]}
							defaultValue={
								selectedCompanyCategory
									? {
											id: selectedCompanyCategory.id,
											title: selectedCompanyCategory.title,
										}
									: undefined
							}
							floating={true}
							portal={true}
							handleSelect={(selected) => {
								setSelectedCompanyCategory({
									id: selected.id as number,
									title: selected.title,
								});
							}}
							label="Company category"
						/>
					</div>
				</div>
				<div>
					<TextArea
						handleChange={(e) => setReason(e.target.value)}
						label="Reason for change"
						name="reason"
						placeholder="Enter reason for this change..."
						showError={false}
						value={reason}
					/>
				</div>
				<div className="flex flex-row gap-2 justify-end">
					<Button
						color={ButtonColor.ACTION}
						title="Save"
						disabled={isLoading}
						onClick={handleSave}
					/>
				</div>
			</div>,
			document.body
		);
	};

	if (!companyResult) {
		return null;
	}

	if (scoreBadge) {
		const scoringSource = getTrackerBadgeScoringSource(companyResult);

		return (
			<>
				{isVisible && renderChangeResult()}
				<Tooltip
					content={
						<LeadScoringPopover
							note={isCompanyOnlyScore(companyResult) ? COMPANY_ONLY_SCORE_NOTE : undefined}
							scoreLabel={scoreBadge}
							scoring={scoringSource}
						/>
					}
					delay={300}
					disabled={!hasLeadScoringContent(scoringSource)}
					panel
				>
					<div
						ref={triggerRef}
						className={`${badgeClassName} mr-1`}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setIsVisible(!isVisible);
						}}
					>
						{scoreBadge}
					</div>
				</Tooltip>
			</>
		);
	}

	return (
		<>
			{isVisible && renderChangeResult()}
			<Tooltip content={"Still ongoing"}>
				<div
					ref={triggerRef}
					className="cursor-pointer w-[24px] h-[24px] text-text-meta text-xxs font-bold bg-border-light rounded-full justify-center items-center flex"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsVisible(!isVisible);
					}}
				>
					<Icon className="text-text-disabled w-6 h-6 p-1" icon={faClock} />
				</div>
			</Tooltip>
		</>
	);
};
